import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface ContractTemplate {
  id: number;
  name: string;
  description: string;
  is_default: boolean;
}

interface Deal {
  id: number;
  title: string;
  value: number;
  currency: string;
  lead_name?: string;
  lead_email?: string;
  lead_company?: string;
}

interface GenerateContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContractGenerated: () => void;
  dealId: number;
  deal?: Deal;
}

const GenerateContractModal: React.FC<GenerateContractModalProps> = ({
  isOpen,
  onClose,
  onContractGenerated,
  dealId,
  deal
}) => {
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({
    company_name: '',
    custom_note: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      // Pre-populate variables with deal information
      if (deal) {
        setVariables({
          company_name: deal.lead_company || '',
          custom_note: `Contract for ${deal.title} - ${deal.value} ${deal.currency}`,
          deal_value: deal.value?.toString() || '',
          deal_currency: deal.currency || '',
          deal_title: deal.title || ''
        });
      }
    }
  }, [isOpen, deal]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/contracts/templates', {
        headers: { 'x-auth-token': token }
      });
      
      const templateList = response.data || [];
      setTemplates(templateList);
      
      // Auto-select default template if available
      const defaultTemplate = templateList.find((t: ContractTemplate) => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate.id);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Failed to load contract templates');
    }
  };

  const handleGenerate = async () => {
    if (!selectedTemplateId) {
      setError('Please select a contract template');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/contracts/generate', {
        template_id: selectedTemplateId,
        deal_id: dealId,
        variables
      }, {
        headers: { 'x-auth-token': token }
      });

      // Success - close modal and refresh contracts
      onContractGenerated();
      onClose();
      
      // Reset form
      setSelectedTemplateId(null);
      setVariables({
        company_name: '',
        custom_note: ''
      });
    } catch (error: any) {
      console.error('Error generating contract:', error);
      setError(error.response?.data?.message || 'Failed to generate contract');
    } finally {
      setLoading(false);
    }
  };

  const handleVariableChange = (key: string, value: string) => {
    setVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold">Generate Contract</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Deal Information */}
          {deal && (
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <h3 className="font-medium text-gray-900 mb-2">Deal Information</h3>
              <div className="text-sm text-gray-600">
                <div><strong>Title:</strong> {deal.title}</div>
                <div><strong>Value:</strong> {deal.value} {deal.currency}</div>
                {deal.lead_name && <div><strong>Customer:</strong> {deal.lead_name}</div>}
              </div>
            </div>
          )}

          {/* Template Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contract Template <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedTemplateId || ''}
              onChange={(e) => setSelectedTemplateId(Number(e.target.value) || null)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select a template</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} {template.is_default ? '(Default)' : ''}
                </option>
              ))}
            </select>
            {templates.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">
                No templates available. Create a template first.
              </p>
            )}
          </div>

          {/* Custom Variables */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Contract Variables</h3>
            
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Company Name</label>
              <input
                type="text"
                value={variables.company_name || ''}
                onChange={(e) => handleVariableChange('company_name', e.target.value)}
                placeholder="Client's company name"
                className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Custom Note</label>
              <textarea
                value={variables.custom_note || ''}
                onChange={(e) => handleVariableChange('custom_note', e.target.value)}
                placeholder="Additional notes or terms for this contract"
                className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                rows={3}
              />
            </div>

            <div className="text-xs text-gray-500">
              <p><strong>Available variables in templates:</strong></p>
              <p>{'{{current_date}}, {{lead_name}}, {{lead_email}}, {{deal_title}}, {{deal_value}}, {{company_name}}, {{custom_note}}'}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={loading || !selectedTemplateId || templates.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Generating...' : 'Generate Contract'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateContractModal;